import { useState, useEffect, useCallback, useMemo, Fragment } from "react"

import {
  ContentCopy,
  ExpandMore,
  ExpandLess,
  UnfoldMore,
  UnfoldLess,
  SmartToy,
  Person,
  Settings,
  Code,
  DataObject,
} from '@mui/icons-material'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Collapse,
  CircularProgress,
  Alert,
  Tooltip,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material'

import { getPrompts } from "../api/services/prompts"

import type { PromptInfo } from "../api/services/prompts"

const TEMPLATE_VARIABLE_PATTERN = /\[([A-Z_]+)\]/

const estimateTokenCount = (text: string): number => {
  return Math.ceil(text.length / 4)
}

const formatTokenCount = (count: number): string => {
  if (count >= 1000) {
    return `~${(count / 1000).toFixed(1)}k`
  }
  return `~${count}`
}

const HighlightedContent = ({ content }: { content: string }) => {
  const parts = content.split(TEMPLATE_VARIABLE_PATTERN)

  return (
    <Typography
      component="pre"
      variant="body2"
      sx={{
        whiteSpace: 'pre-wrap',
        fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
        fontSize: '0.75rem',
        lineHeight: 1.5,
        margin: 0,
        color: 'text.primary',
      }}
    >
      {parts.map((part, index) => {
        const isVariable = index % 2 === 1
        if (isVariable) {
          return (
            <Box
              key={index}
              component="span"
              sx={{
                bgcolor: 'primary.50',
                color: 'primary.main',
                fontWeight: 600,
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                border: 1,
                borderColor: 'primary.200',
                fontSize: '0.75rem',
              }}
            >
              {'{'}{part}{'}'}
            </Box>
          )
        }
        return <Fragment key={index}>{part}</Fragment>
      })}
    </Typography>
  )
}

const PromptCard = ({
  prompt,
  isExpanded,
  onToggle,
  onCopy,
}: {
  prompt: PromptInfo
  isExpanded: boolean
  onToggle: () => void
  onCopy: () => void
}) => {
  const tokenCount = useMemo(() => estimateTokenCount(prompt.content), [prompt.content])
  const isSystem = prompt.type === 'System'

  return (
    <Card
      sx={{
        border: 1,
        borderColor: isExpanded ? 'primary.200' : 'grey.200',
        boxShadow: isExpanded ? 1 : 0,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'primary.200',
        },
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
          }}
          onClick={onToggle}
        >
          <Box
            sx={{
              p: 0.5,
              borderRadius: 1,
              bgcolor: isSystem ? 'success.50' : 'warning.50',
              color: isSystem ? 'success.main' : 'warning.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {isSystem ? <SmartToy sx={{ fontSize: '1rem' }} /> : <Person sx={{ fontSize: '1rem' }} />}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {prompt.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: 'none', sm: 'block' } }}>
                {prompt.description}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Chip
              label={prompt.type}
              size="small"
              color={isSystem ? 'success' : 'warning'}
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
            {prompt.parameters.length > 0 && (
              <Chip
                icon={<DataObject sx={{ fontSize: '0.8rem !important' }} />}
                label={`${prompt.parameters.length}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            )}
            <Chip
              label={formatTokenCount(tokenCount)}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
            <Tooltip title="Copy prompt content">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  onCopy()
                }}
                sx={{ color: 'text.secondary', p: 0.5 }}
              >
                <ContentCopy sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" sx={{ color: 'text.secondary', p: 0.5 }}>
              {isExpanded ? <ExpandLess sx={{ fontSize: '1.1rem' }} /> : <ExpandMore sx={{ fontSize: '1.1rem' }} />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={isExpanded}>
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'grey.100' }}>
            {prompt.parameters.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Settings sx={{ fontSize: '0.85rem' }} />
                  Parameters
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {prompt.parameters.map((param) => (
                    <Chip
                      key={param.name}
                      icon={<Code sx={{ fontSize: '0.75rem !important' }} />}
                      label={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{param.name}</Box>
                          <Box component="span" sx={{ color: 'text.secondary' }}>=</Box>
                          <Box component="span" sx={{ color: 'text.secondary' }}>{param.currentValue}</Box>
                        </Box>
                      }
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 24, bgcolor: 'grey.50' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box
              sx={{
                bgcolor: 'grey.50',
                borderRadius: 1,
                p: 1.5,
                maxHeight: 400,
                overflow: 'auto',
                border: 1,
                borderColor: 'grey.200',
              }}
            >
              <HighlightedContent content={prompt.content} />
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  )
}

export default function PromptsPage() {
  const [prompts, setPrompts] = useState<PromptInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedPrompts, setExpandedPrompts] = useState<Set<string>>(new Set())
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState(0)

  useEffect(() => {
    const loadPrompts = async () => {
      try {
        setLoading(true)
        const data = await getPrompts()
        setPrompts(data.sort((a, b) => a.order - b.order))
        setError(null)
      } catch (err) {
        setError('Failed to load prompts')
        console.error('Error loading prompts:', err)
      } finally {
        setLoading(false)
      }
    }

    loadPrompts()
  }, [])

  const categories = useMemo(() => {
    const cats = [...new Set(prompts.map((p) => p.category))]
    return ['All', ...cats]
  }, [prompts])

  const filteredPrompts = useMemo(() => {
    if (activeTab === 0) return prompts
    const category = categories[activeTab]
    return prompts.filter((p) => p.category === category)
  }, [prompts, activeTab, categories])

  const allExpanded = useMemo(
    () => filteredPrompts.length > 0 && filteredPrompts.every((p) => expandedPrompts.has(p.id)),
    [filteredPrompts, expandedPrompts],
  )

  const togglePrompt = useCallback((id: string) => {
    setExpandedPrompts((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    setExpandedPrompts((prev) => {
      const next = new Set(prev)
      if (allExpanded) {
        filteredPrompts.forEach((p) => next.delete(p.id))
      } else {
        filteredPrompts.forEach((p) => next.add(p.id))
      }
      return next
    })
  }, [allExpanded, filteredPrompts])

  const copyPromptContent = useCallback(async (prompt: PromptInfo) => {
    try {
      await navigator.clipboard.writeText(prompt.content)
      setSnackbarMessage(`Copied "${prompt.name}" to clipboard`)
    } catch {
      setSnackbarMessage('Failed to copy to clipboard')
    }
  }, [])

  if (loading) {
    return (
      <Box sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ flex: 1, p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  const totalTokens = prompts.reduce((sum, p) => sum + estimateTokenCount(p.content), 0)

  return (
    <Box sx={{ flex: 1, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h5" component="h1" fontWeight="bold">
            AI Prompts
          </Typography>
          <Chip
            label={`${prompts.length} prompts \u00B7 ${formatTokenCount(totalTokens)} tokens`}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          />
        </Box>
        <Tooltip title={allExpanded ? 'Collapse all' : 'Expand all'}>
          <IconButton onClick={toggleAll} size="small">
            {allExpanded ? <UnfoldLess /> : <UnfoldMore />}
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_e, v: number) => setActiveTab(v)}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { textTransform: 'none', minHeight: 36, py: 0.5, fontSize: '0.8rem' },
          }}
        >
          {categories.map((category) => {
            const count = category === 'All'
              ? prompts.length
              : prompts.filter((p) => p.category === category).length
            return (
              <Tab
                key={category}
                label={`${category} (${count})`}
              />
            )
          })}
        </Tabs>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {filteredPrompts.map((prompt) => (
          <PromptCard
            key={prompt.id}
            prompt={prompt}
            isExpanded={expandedPrompts.has(prompt.id)}
            onToggle={() => togglePrompt(prompt.id)}
            onCopy={() => copyPromptContent(prompt)}
          />
        ))}
      </Box>

      {filteredPrompts.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No prompts found in this category.
          </Typography>
        </Box>
      )}

      <Snackbar
        open={snackbarMessage !== null}
        autoHideDuration={2500}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  )
}
